import React from "react";
import { Alert, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatDay } from "./constants";

// The server computes `terms` (utils/vendorContracts/terms.js); this only presents them.

const urgencyColor = contract => {
  const days = contract.terms.daysToDeadline;
  const warnFrom = contract.reminderDays ?? 30;
  if (days === null) return "default";
  if (days <= Math.min(7, warnFrom)) return "red";
  if (days <= warnFrom) return "orange";
  return "green";
};

/** Compact deadline for the list. */
export const DeadlineCell = ({ contract, lng }) => {
  const { t } = useTranslation("dashboard");
  const { state, deadline, daysToDeadline, termEnd, earliestEnd } = contract.terms;
  if ((state === "renews" || state === "unknown") && deadline) {
    return (
      <>
        <div>{formatDay(deadline, lng)}</div>
        <Tag color={urgencyColor(contract)}>
          {daysToDeadline <= 0 ? t("Today") : t("{{count}} days left", { count: daysToDeadline })}
        </Tag>
      </>
    );
  }
  if (state === "anytime") {
    return (
      <>
        <Tag color="blue">{t("Any time")}</Tag>
        {earliestEnd && (
          <Typography.Text type="secondary" className="vc-small">
            {t("ends {{date}} at the earliest", { date: formatDay(earliestEnd, lng) })}
          </Typography.Text>
        )}
      </>
    );
  }
  if (state === "ends")
    return <Tag>{t("Not needed – ends {{date}}", { date: formatDay(termEnd, lng) })}</Tag>;
  if (state === "cancelled") return <Tag color="default">{t("Cancelled")}</Tag>;
  if (state === "ended") return <Tag color="default">{t("Ended")}</Tag>;
  return <Tag color="warning">{t("Terms incomplete")}</Tag>;
};

/** One sentence at the top of the drawer: what to do by when. */
const TermsSummary = ({ contract, lng }) => {
  const { t } = useTranslation("dashboard");
  if (contract.extraction?.status === "processing") return null;
  const { state, deadline, termEnd, earliestEnd } = contract.terms;
  const date = value => formatDay(value, lng);

  let type = "info";
  let message;
  if ((state === "renews" || state === "unknown") && deadline) {
    const color = urgencyColor(contract);
    type = color === "red" ? "error" : color === "orange" ? "warning" : "success";
    message = t("Cancel by {{deadline}} to end the contract on {{end}}.", {
      deadline: date(deadline),
      end: date(termEnd)
    });
    if (state === "unknown") message += ` ${t("What happens after that is not known.")}`;
  } else if (state === "renews") {
    type = "warning";
    message = t("The minimum term ends on {{end}}. Add the notice period to get the deadline.", {
      end: date(termEnd)
    });
  } else if (state === "anytime") {
    message = earliestEnd
      ? t("Can be cancelled at any time. Cancelled today, it ends on {{end}}.", {
          end: date(earliestEnd)
        })
      : t("Can be cancelled at any time.");
  } else if (state === "ends") {
    message = t("Ends automatically on {{end}}. No cancellation needed.", { end: date(termEnd) });
  } else if (state === "cancelled") {
    message = t("Cancelled. Ends on {{end}}.", { end: date(termEnd) });
  } else if (state === "ended") {
    message = t("This contract has ended.");
  } else {
    type = "warning";
    message = t(
      "The cancellation deadline cannot be calculated yet. Add the start, the minimum term and the notice period."
    );
  }
  return <Alert type={type} showIcon message={message} className="mb-3" />;
};

export default TermsSummary;
